import type { Traveler } from "@b2bportal/traveler-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { specialCharacterRegex } from "@hopper-b2b/utilities";
import dayjs from "dayjs";

export enum PassportFormFields {
  Nationality = "nationality",
  PassportNumber = "passportNumber",
  PassportExpiryDate = "passportExpiryDate",
  PassportIssuanceCountry = "passportIssuanceCountry",
}

export type PassengerFormContext = {
  [PassportFormFields.Nationality]: string;
  [PassportFormFields.PassportNumber]: string;
  [PassportFormFields.PassportExpiryDate]: string;
  [PassportFormFields.PassportIssuanceCountry]: string;
};

export const usePassportFormFields = ({
  dateFormat = "DD/MM/YYYY",
  traveler,
  requirePassportDetails = true,
  prefillNationality = true,
}: {
  dateFormat?: string;
  traveler?: Traveler;
  requirePassportDetails?: boolean;
  // When true (default), nationality is pre-filled with the brand's
  // preferred country code. Set false to leave it empty (HSBC-1316).
  // Note: the form's `nationality` field is shared with TravellerDetails —
  // its default must agree with `useTravellerDetailsFormFields`, otherwise
  // whichever spreads last wins in the merged defaults.
  prefillNationality?: boolean;
}) => {
  const { t, brand } = useI18nContext();

  const passportDetailsDefaultValues: PassengerFormContext = {
    [PassportFormFields.Nationality]: prefillNationality
      ? brand.preferredCountryCode
      : "",
    [PassportFormFields.PassportNumber]: "",
    [PassportFormFields.PassportExpiryDate]: "",
    [PassportFormFields.PassportIssuanceCountry]: brand.preferredCountryCode,
  };

  const formFields = {
    [PassportFormFields.Nationality]: {
      ...(requirePassportDetails && {
        required: t("travelerForm.errors.nationalityRequired"),
      }),
    },
    [PassportFormFields.PassportNumber]: {
      ...(requirePassportDetails && {
        required: t("travelerForm.errors.passportRequired"),
      }),
      validate: (passportNumber: string) => {
        if (!passportNumber) return undefined;
        if (/\s/.test(passportNumber)) {
          return t("noWhitespaceError");
        }
        if (
          // we allow special characters (specifically *) is the passport number hasn't changed
          // this is to allow masking on the backend
          traveler?.airTravelerDetails?.passport?.number !== passportNumber &&
          !specialCharacterRegex.test(passportNumber)
        ) {
          return t("noSpecialCharactersError");
        }
      },
    },
    [PassportFormFields.PassportExpiryDate]: {
      ...(requirePassportDetails && {
        required: t("travelerForm.errors.passportExpirationRequired"),
      }),
      validate: (passportExpiryDate: string) => {
        if (!passportExpiryDate) return undefined;
        const dateDayjs = dayjs(passportExpiryDate, dateFormat, true);
        if (!dateDayjs.isValid() || dateDayjs.isBefore(dayjs())) {
          return t("enterAValidExpiry");
        }
      },
    },
    [PassportFormFields.PassportIssuanceCountry]: {
      ...(requirePassportDetails && {
        required: t("travelerForm.errors.issueCountryRequired"),
      }),
    },
  };

  return { formFields, passportDetailsDefaultValues };
};
